import React from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
} from '@heroicons/react/solid';

/**
 * Subtle text link for jumping between major appointment phases (History ↔ SSI
 * ↔ Review) while the primary footer buttons stay focused on within-phase
 * steps (History sections, DSM criteria). Matches the ADHD SSI skip-link pattern.
 */
export function ModularPhaseSkipLink({
  label,
  onClick,
  direction = 'forward',
  disabled = false,
  title,
  className = '',
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`
        flex items-center text-xs text-gray-500 hover:text-primary font-medium
        hover:underline transition-all duration-200 mt-1.5 group
        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:no-underline
        ${className}
      `}
    >
      {direction === 'back' && (
        <ArrowLeftIcon className="w-3 h-3 mr-1 group-hover:text-primary" aria-hidden="true" />
      )}
      <span>{label}</span>
      {direction === 'forward' && (
        <ArrowRightIcon className="w-3 h-3 ml-1 group-hover:text-primary" aria-hidden="true" />
      )}
    </button>
  );
}

/**
 * Shared fixed footer for the Adult Autism modular assessment modules.
 *
 * WHY THIS EXISTS
 * Every wizard-style Autism module (History, SSI, Observation, and now DD/PAD)
 * pins a "Back | progress counter | Next/Finish" bar to the bottom of the
 * scrollable content column. It was previously inlined per module; this is the
 * single source of truth so the spacing, colours and behaviour match everywhere.
 *
 * WITHIN-PHASE VS BETWEEN-PHASE NAVIGATION
 * Primary button CTAs move within the current phase (History section → section,
 * SSI criterion → criterion). Optional phaseBackLink / phaseForwardLink render
 * as smaller text links beneath those CTAs to jump between History, SSI, and
 * Review — the same visual hierarchy as the Adult ADHD SSI proforma. When the
 * only backward option is an earlier phase (e.g. first SSI criterion), that
 * phase jump stays a full button via onModuleExit instead of a link.
 *
 * LAYOUT CONTRACT
 * This component renders ONLY the footer bar. It is designed to live as the last
 * child of a flex column (`flex flex-col`) whose parent has a bounded height
 * (e.g. `flex h-[calc(100vh-8rem)]`), with the scrollable content as the
 * preceding `flex-1 overflow-y-auto` sibling. The footer itself is
 * `flex-shrink-0 border-t`, so it stays visible while the content scrolls.
 *
 * @param {object} props
 * @param {() => void} [props.onBack]            Back handler. If omitted, the slot stays empty.
 * @param {string}  [props.backLabel='Back']
 * @param {boolean} [props.showBack=true]        Hide Back entirely (e.g. first step).
 * @param {boolean} [props.backDisabled=false]
 * @param {() => void} [props.onModuleExit]      Leave the current module as a full button when
 *                                               it is the sole backward action (e.g. first SSI
 *                                               criterion → Back to History). Rendered beside
 *                                               step-back when both exist; otherwise alone on left.
 * @param {string}  [props.moduleExitLabel]      Label for onModuleExit (required when handler set).
 * @param {{ label: string, onClick: () => void, disabled?: boolean }} [props.phaseBackLink]
 *        Optional link below the left CTA(s) for cross-phase backward navigation.
 * @param {{ label: string, onClick: () => void, disabled?: boolean, title?: string }} [props.phaseForwardLink]
 *        Optional link below the right CTA(s) for cross-phase forward navigation.
 * @param {React.ReactNode} [props.counterText]  Centre text, e.g. "2 of 4".
 * @param {() => void} [props.onNext]            Next handler (the default right-side CTA).
 * @param {string}  [props.nextLabel='Next']
 * @param {boolean} [props.nextDisabled=false]
 * @param {boolean} [props.showNext=true]
 * @param {React.ReactNode} [props.rightActions] Custom right-side content. When provided it
 *                                               REPLACES the default Next button (used for
 *                                               terminal steps with bespoke forward actions, e.g.
 *                                               History's "Go to SSI" or a "Finish" button).
 */
export default function ModularStepFooter({
  onBack,
  backLabel = 'Back',
  showBack = true,
  backDisabled = false,
  onModuleExit,
  moduleExitLabel,
  phaseBackLink,
  phaseForwardLink,
  counterText,
  onNext,
  nextLabel = 'Next',
  nextDisabled = false,
  showNext = true,
  rightActions,
}) {
  const moduleExitButtonClass = 'flex items-center py-2 px-4 rounded-md border transition-all duration-200 text-gray-700 border-gray-300 hover:bg-gray-50';

  const defaultForwardButton = showNext && onNext && (
    <button
      onClick={onNext}
      disabled={nextDisabled}
      className={`flex items-center py-2 px-4 rounded-md shadow-sm transition-all duration-200 text-white
        ${nextDisabled
          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
          : 'bg-primary hover:bg-primary/90'}`}
    >
      <span className="font-semibold text-sm">{nextLabel}</span>
      <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
    </button>
  );

  const forwardButtons = rightActions !== undefined ? rightActions : defaultForwardButton;

  return (
    <div className="flex-shrink-0 border-t border-gray-200 bg-white px-8 py-3 flex items-center">
      {/* Left — within-phase back button(s); cross-phase link sits underneath. */}
      <div className="flex flex-1 flex-col items-start justify-center min-w-0">
        <div className="flex items-center justify-start gap-3 min-w-0">
          {showBack && onBack && (
            <button
              onClick={onBack}
              disabled={backDisabled}
              className="flex items-center py-2 px-4 rounded-md border transition-all duration-200
                text-gray-700 border-gray-300 hover:bg-gray-50
                disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
              <span className="font-semibold text-sm">{backLabel}</span>
            </button>
          )}
          {onModuleExit && moduleExitLabel && (
            <button
              type="button"
              onClick={onModuleExit}
              className={moduleExitButtonClass}
            >
              <span className="font-semibold text-sm">{moduleExitLabel}</span>
            </button>
          )}
        </div>
        {phaseBackLink && (
          <ModularPhaseSkipLink
            label={phaseBackLink.label}
            onClick={phaseBackLink.onClick}
            direction="back"
            disabled={phaseBackLink.disabled}
          />
        )}
      </div>

      {/* Centre counter (optional). */}
      {counterText != null && (
        <span className="text-sm text-gray-400 font-medium flex-shrink-0 px-4">{counterText}</span>
      )}

      {/* Right — within-phase forward CTA(s); cross-phase link sits underneath. */}
      <div className="flex flex-1 flex-col items-end justify-center min-w-0">
        {forwardButtons && (
          <div className="flex justify-end gap-3 min-w-0">
            {forwardButtons}
          </div>
        )}
        {phaseForwardLink && (
          <ModularPhaseSkipLink
            label={phaseForwardLink.label}
            onClick={phaseForwardLink.onClick}
            direction="forward"
            disabled={phaseForwardLink.disabled}
            title={phaseForwardLink.title}
          />
        )}
      </div>
    </div>
  );
}
